import React from 'react';
import QldLlnStep from './QldLlnStep';
import QldEoiForm from './QldEoiForm';
import QldAssessmentFlow from './QldAssessmentFlow';
import QldDeclarationStep from './QldDeclarationStep';
import qldAssessmentQuestions from '../data/qldAssessmentQuestions';
import qldAssessment2Questions from '../data/qldAssessment2Questions';
import qldAssessment3Questions from '../data/qldAssessment3Questions';
import qldAssessment4Questions from '../data/qldAssessment4Questions';
import { buildQldFullName, QLD_STAGE_LABELS } from '../qldWorkflow';
import { trainers } from '../constants';
import { getQldMobile } from '../qldContact';

const assessmentQuestionMap = {
    assessment1: qldAssessmentQuestions,
    assessment2: qldAssessment2Questions,
    assessment3: qldAssessment3Questions,
    assessment4: qldAssessment4Questions,
};

const hasManualAnswerSection = (question) => (
    Boolean(question?.textAnswer)
    || Boolean(question?.partBQuestion)
    || ['form', 'tableForm', 'imageTableForm', 'textTableForm'].includes(question?.type)
);

const assessmentPrefaceMap = {
    assessment1: [
        {
            heading: 'Introduction',
            subheading: '',
            body: [
                'Welcome to the assessment for the unit <b><i>CPCWHS1001 Prepare to Work Safely in the Construction Industry</i></b>. This document includes everything you need to complete the assessment relating to this subject.',
                'Please read the instructions for each assessment task prior to commencing the assessment and if you have any questions, please ask your assessor.',
                'The instructions will tell you:',
                '<ul style="margin:8px 0 8px 24px; padding:0;"><li>what you need to do to complete the assessment task</li><li>what you need to do to pass each assessment.</li></ul>',
                'Summary of Unit of Competency',
                'This unit of competency specifies the mandatory work health and safety training required prior to undertaking construction work. The unit requires you to demonstrate personal awareness and knowledge of health and safety legislative requirements in order to work safely and prevent injury or harm to yourself and others.',
                'It covers identifying and orally reporting common construction hazards, understanding basic risk control measures, and identifying procedures for responding to potential incidents and emergencies. It also covers correctly selecting and fitting common personal protective equipment (PPE) used for construction work.',
                'Learning outcomes',
                'By completing this assessment, you will be demonstrating your ability to:',
                '<ol style="margin:8px 0 8px 24px; padding:0;"><li>Identify health and safety legislative requirements of construction work.</li><li>Identify construction hazards and risk control measures.</li><li>Identify health and safety communication and reporting processes.</li><li>Identify incident and emergency response procedures.</li></ol>',
                'Methods of assessment',
                'Assessment of this unit consists of four (4) assessment tasks:',
                '<ol style="margin:8px 0 8px 24px; padding:0;"><li>Assessment task one (AT1) – multiple choice and short answer questions.</li><li>Assessment task two (AT2) – questions requiring oral responses.</li><li>Assessment task three (AT3) – complete a simple form.</li><li>Assessment task four (AT4) – demonstration of correctly fitting PPE.</li></ol>',
                'Resources required',
                'You will require the following PPE to undertake AT4:',
                '<ul style="margin:8px 0 8px 24px; padding:0;"><li>Eye protection</li><li>Hearing protection</li><li>Hard hat</li><li>High visibility vest, shirt or jacket.</li></ul>',
                'You will also require access to a copy (either electronic or hard copy) of the <b><i>Work Health and Safety Act 2011 (Qld) (the WHS Act)</i></b>.',
                'Reasonable Adjustment',
                'In Australia, federal legislation and the standards governing Registered Training Organisations (RTOs) require reasonable adjustments to be made in the support of learners with disabilities to allow for their equitable participation without compromising course integrity.',
                'If you have a disability, medical condition, learning need or other circumstance that may affect your assessment, you may request a reasonable adjustment. Adjustments may include modifying delivery methods, assessments, materials, training location or using assistive technologies tailored to individual needs and context.',
                'Any adjustment will be designed to support your participation while still requiring you to meet all the competency requirements of the unit. While you are not required to disclose a disability, it is recommended to enable timely support.',
                'Please speak with your trainer or assessor as early as possible if you think you may need this support.',
            ],
            buttonLabel: 'Next Page',
        },
        {
            heading: 'Assessment Task One (1)',
            subheading: 'Multiple Choice and short answer questions',
            body: [
                'Context of the Assessment',
                'You must complete the questions without assistance.',
                'If the assessor leaves the room to conduct an individual assessment task with another student, you must not speak to other students.',
                'Instructions for Student',
                'Answer the following multiple choice questions by placing a <b>&#10003;</b> in the box next to the correct answer/s. Answer the short answer questions by writing an answer in the space provided.',
                'Some questions in this assessment contain two (2) parts (Part A and Part B). You must respond to both parts correctly for the entire question to be marked as correct.',
                'The assessor will clarify any illegible, partially correct or unanswered questions with you during marking.',
                'Criteria for Success',
                'You must answer all of the questions in a way that demonstrates an understanding of the question being asked.',
                'All answers must be correct to achieve a satisfactory result.',
            ],
            buttonLabel: 'Next Page',
        },
    ],
    assessment2: [
        {
            heading: 'Assessment Task Two (2)',
            subheading: 'Oral Questions',
            body: [
                'Context of the Assessment',
                'In this assessment task, you must listen to instructions given to you by the assessor in order to identify key safety information.',
                'You must orally answer the questions independently, away from other students.',
                'The assessment must be completed in the presence of an assessor.',
                'Instructions for Student',
                'Listen to the questions as they are read to you by your assessor and communicate your response to the assessor orally. The assessor will record information in the space provided.',
                'You may ask questions to clarify instructions given by the assessor.',
                'If you do not understand a question being asked, you may request clarification from your assessor.',
                'Criteria for Success',
                'You must answer all the questions in a way that demonstrates an understanding of the question being asked.',
                'All answers must be correct to achieve a competent result.',
            ],
            buttonLabel: 'Next Page',
        },
    ],
    assessment3: [
        
    ],
    assessment4: [
        {
            heading: 'Assessment Task Four (4)',
            subheading: 'Fitting PPE (Observation)',
            body: [
                'Context of the Assessment',
                'The assessment is a practical task and must be directly observed by your assessor. You must complete this assessment task without input from others.',
                'Instructions for Student',
                'For this task, you need to demonstrate the correct fitting of the following Personal Protective Equipment (PPE): Eye protection; Hearing protection; Hard hat; High visibility vest, shirt or jacket.',
                'The assessor will ask you to fit one item of PPE at a time. You will fit the PPE and then remove it before fitting the next item.',
                'You must ask at least one question to clarify instructions given by the assessor.',
                'Criteria for Success',
                'To achieve a competent result, you must correctly fit each item of PPE.',
            ],
            buttonLabel: 'Next Page',
        },
    ],
};

const QldStageRenderer = ({
    stageKey,
    studentDoc,
    trainerMode = false,
    onStageComplete,
    onStageProgress,
    assessorReviewData,
    onAssessorReviewChange,
    hideTrainerDecisionActions = false,
}) => {
    if (!stageKey || !studentDoc) {
        return null;
    }

    if (stageKey === 'lln') {
        const trainerName = trainers.find((row) => row.trainerId === studentDoc.trainerId)?.name || studentDoc.trainerId || '';
        return (
            <QldLlnStep
                initialData={studentDoc.responses?.lln || {}}
                studentNameOverride={buildQldFullName(studentDoc)}
                trainerNameOverride={trainerName}
                deliveryOverride='Online'
                trainerMode={trainerMode}
                hideTrainerDecisionActions={hideTrainerDecisionActions}
                onProgress={onStageProgress}
                onComplete={onStageComplete}
            />
        );
    }

    if (stageKey === 'eoi') {
        return (
            <QldEoiForm
                embedded
                trainerMode={trainerMode}
                hideTrainerDecisionActions={hideTrainerDecisionActions}
                trainerIdOverride={studentDoc.trainerId}
                studentNameOverride={buildQldFullName(studentDoc)}
                mobileOverride={getQldMobile(studentDoc)}
                initialData={studentDoc.responses?.eoi || {}}
                onProgress={onStageProgress}
                onComplete={onStageComplete}
            />
        );
    }

    if (assessmentQuestionMap[stageKey]) {
        const stageState = studentDoc.stages?.[stageKey] || {};
        const retryQuestionIndexes = !trainerMode && stageState.reviewStatus === 'needs_resubmission'
            ? (studentDoc.responses?.[stageKey]?.manualRetryQuestionIndexes || [])
            : [];
        const manualQuestionIndexes = trainerMode
            ? (assessmentQuestionMap[stageKey] || []).reduce((acc, question, index) => {
                if (hasManualAnswerSection(question)) {
                    acc.push(index);
                }

                return acc;
            }, [])
            : [];
        const trainerVisibleQuestionIndexes = trainerMode
            ? (
                stageState.reviewStatus === 'submitted_for_review'
                && Object.keys(studentDoc.trainerResponses?.[stageKey]?.questionMarks || {}).length > 0
                && (studentDoc.responses?.[stageKey]?.manualRetryQuestionIndexes || []).length > 0
            )
                ? (studentDoc.responses?.[stageKey]?.manualRetryQuestionIndexes || [])
                : (stageKey === 'assessment1' ? manualQuestionIndexes : [])
            : [];

        return (
            <QldAssessmentFlow
                embedded
                title={QLD_STAGE_LABELS[stageKey]}
                questionsData={assessmentQuestionMap[stageKey]}
                startIndex={trainerMode && stageKey === 'assessment1' ? 3 : 0}
                assessorMode={trainerMode}
                initialResponseDocument={studentDoc.responses?.[stageKey] || { responses: {}, finalQIndex: 0 }}
                onResponseDocumentChange={onStageProgress}
                onAssessmentComplete={onStageComplete}
                collectStudentInfoOnComplete={false}
                allowReviewCompleted={trainerMode}
                showMarkAllAsC
                assessorReviewData={assessorReviewData}
                onAssessorReviewChange={onAssessorReviewChange}
                retryQuestionIndexes={retryQuestionIndexes}
                visibleQuestionIndexes={trainerVisibleQuestionIndexes}
                reviewFeedbackData={studentDoc.trainerResponses?.[stageKey]?.questionMarks || {}}
                prefaceScreens={assessmentPrefaceMap[stageKey] || []}
                studentQuestionOnly={false}
                inlineAssessorGuideMode={false}
                readOnlyStudentResponses={trainerMode && ['assessment1', 'assessment3'].includes(stageKey)}
                hideQuestionNumbers={['assessment3', 'assessment4'].includes(stageKey)}
                inlineMarkingMode={trainerMode && stageKey === 'assessment2'}
                rightAlignedMarkingMode={trainerMode && stageKey === 'assessment3'}
                embeddedMarkAllCMode={trainerMode && stageKey === 'assessment4'}
                emphasizeQuestionBorder={!trainerMode && stageKey === 'assessment4'}
                documentVariant={trainerMode ? `${stageKey}-assessor` : `${stageKey}-student`}
            />
        );
    }

    if (stageKey === 'declaration') {
        const trainerName = trainers.find((row) => row.trainerId === studentDoc.trainerId)?.name || studentDoc.trainerId || '';
        return (
            <QldDeclarationStep
                trainerMode={trainerMode}
                studentDoc={studentDoc}
                trainerName={trainerName}
                initialData={studentDoc.responses?.declaration || {}}
                onProgress={onStageProgress}
                onComplete={onStageComplete}
            />
        );
    }

    return null;
};

export default QldStageRenderer;
